import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable, throwError } from "rxjs";
import { catchError, delay, tap } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { CREDENTIALS_REQUEST, CREDENTIALS_RESPONSE } from "@rrsolutionsit/atl-chatbot-lib";
import { LocalStorageService, UserLocalStorageObject } from "../shared/local-storage.service";
import { PilotRole } from "../shared/models/pilotRole.model";
import { Country } from "../shared/shared.service";

class LoginReturnData {
    sessionToken: string;
    htlAccount: {
        email: string;
        firstName: string;
        lastName: string;
        organizationId: number;
        showPilotHourLimits: boolean;
    };
    countries: Country[];
    pilotRoles: PilotRole[];
    htlAccountTypes: any;
}

function encodePassword(password: string): string {
    return btoa(String.fromCharCode(...new TextEncoder().encode(password)));
}

export class LoginReturnError {
    status: number;
    stringCode: string;
    value: string | { waitTime: string };
}

@Injectable({
    providedIn: "root",
})
export class LoginService {
    userData = new BehaviorSubject<UserLocalStorageObject>(null);

    private _http = inject(HttpClient);
    private _router = inject(Router);
    private _localStorageService = inject(LocalStorageService);

    constructor() {
        window.addEventListener("message", (event: MessageEvent) => {
            if (event.origin !== window.location.origin || event.data?.type !== CREDENTIALS_REQUEST) {
                return;
            }
            const current = this.currentUserDataValue;
            if (current) {
                this._publishChatbotCredentials(current);
            }
        });
    }

    loginPost(username: string, password: string, turnstileToken: string) {
        const params: any = { username, password: encodePassword(password), turnstileToken };
        if (environment.local) delete params.turnstileToken;

        return this._http.post<LoginReturnData>(environment.serverUrl + "htlAccount/login", params).pipe(
            delay(500),
            catchError(this._handleError),
            tap((resData: any) => {
                const userName = resData.htlAccount.firstName + " " + resData.htlAccount.lastName;
                const userData = new UserLocalStorageObject(
                    resData.htlAccount.email,
                    resData.sessionToken,
                    userName,
                    resData.htlAccount.organizationId,
                    resData.htlAccount.showPilotHourLimits,
                    resData.htlAccountTypes,
                    resData.appConfig,
                    resData.exportPresetFilters,
                );

                localStorage.setItem(environment.userSessionUniqueId, JSON.stringify(userData));
                const sessionObject = this._localStorageService.getAppLocalStorageObject();
                if (!sessionObject.countries) {
                    this._localStorageService.setAppLocalStorage("countries", JSON.stringify(resData.countries));
                }
                this.userData.next(userData);
                this._publishChatbotCredentials(userData);
            }),
        );
    }

    public get currentUserDataValue(): UserLocalStorageObject {
        return this.userData.value;
    }

    autoLogin() {
        const userData: UserLocalStorageObject = JSON.parse(localStorage.getItem(environment.userSessionUniqueId));
        if (!userData) {
            return;
        }
        const loadedUser = new UserLocalStorageObject(
            userData.username,
            userData.sessionToken,
            userData.userName,
            userData.organizationId,
            userData.showPilotHourLimits,
            userData.htlAccountTypes,
            userData.appConfigs,
            userData.exportPresetFilters,
            userData.displayedColumns,
        );
        if (loadedUser.sessionToken) {
            this.userData.next(loadedUser);
            this._publishChatbotCredentials(loadedUser);
        }
    }

    /**
     * Hands the current session token to the chatbot, which listens for this same message on this
     * very window. It is sent on every login and on every restored session, because the widget
     * requests credentials each time it mounts and may mount long after login.
     *
     * Only the token: the AI backend reads who the caller is out of it, so an account id sent
     * alongside would be a value nothing checks.
     */
    private _publishChatbotCredentials(userData: UserLocalStorageObject): void {
        if (!userData.sessionToken) {
            return;
        }
        window.postMessage({ type: CREDENTIALS_RESPONSE, token: userData.sessionToken }, window.location.origin);
    }

    logout() {
        this.userData.next(null);
        localStorage.removeItem(environment.userSessionUniqueId);
        // localStorage.removeItem(environment.appSessionUniqueId);
        this._router.navigate(["/login"]);
    }

    private _handleError(reponseError: HttpErrorResponse): Observable<LoginReturnError> {
        let errorCode: LoginReturnError;
        switch (reponseError.status) {
            case 604:
                errorCode = { status: reponseError.status, stringCode: "accountDisabledError", value: reponseError.error };
                break;
            case 662:
                errorCode = { status: reponseError.status, stringCode: "multipleFailedLoginError", value: reponseError.error };
                break;
            case 663:
                errorCode = { status: reponseError.status, stringCode: "accountBlockedError", value: reponseError.error };
                break;
            case 615:
                errorCode = { status: reponseError.status, stringCode: "failedCaptchaError", value: reponseError.error };
                break;
            default:
                errorCode = { status: reponseError.status, stringCode: "wrongCredentialsError", value: reponseError.error };
                break;
        }

        return throwError(errorCode);
    }
}
